// src/modules/app-views/crm/pages/Boards/modals/BoardSettingsModal/tabs/ParametersTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Button, Form, Grid, Input, Select, Tooltip } from 'antd';
import SimpleColorPicker from 'components/SimpleComponents/SimpleColorPicker';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ParametersTab = ({ form, isEditing, isOrphaned, boardGroups, resolvedGroupUid, setResolvedGroupUid, onDelete, onArchive }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const storedTeam = useZustand('auth', 'team');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const teamOptions = useMemo(() => buildTeamOptions(storedTeam), [storedTeam]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Form form={form} layout="vertical" className="mt-3">
      {/* Title + ColorPicker inline — required prop shows the red asterisk on the label */}
      <Form.Item label={translateX('title')} required>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Form.Item
            name="title"
            noStyle
            rules={[{ required: true, message: () => translateX('required_field') }]}
          >
            <Input placeholder={translateX('board_title')} autoFocus style={{ flex: 1 }} />
          </Form.Item>
          <Form.Item name="backColor" noStyle>
            <SimpleColorPicker />
          </Form.Item>
        </div>
      </Form.Item>

      <Form.Item name="description" label={translateX('description')}>
        <Input.TextArea rows={3} placeholder={translateX('description')} />
      </Form.Item>

      {/* Team access */}
      <Form.Item
        name="teamAccessIds"
        label={translateX('team')}
        tooltip={translateX('team_access_tooltip')}
      >
        <Select
          mode="multiple"
          options={teamOptions}
          placeholder={translateX('all_team_members')}
          allowClear
          optionFilterProp="label"
          tagRender={({ value, closable, onClose }) => {
            const opt = teamOptions.find(o => o.value === value);
            if (!opt) return null;
            return (
              <Tooltip title={opt.label}>
                <span
                  onClick={e => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', marginRight: 2, cursor: 'default' }}
                >
                  <SimpleAvatar
                    size={24}
                    src={opt.avatarSrc}
                    name={opt.label}
                    backColor={opt.backColor}
                    foreColor={opt.foreColor}
                    showBorder={false}
                    showShadow={false}
                  />
                  {closable && (
                    <span onClick={onClose} style={{ marginLeft: 2, cursor: 'pointer', fontSize: 10, color: '#999' }}>
                      &times;
                    </span>
                  )}
                </span>
              </Tooltip>
            );
          }}
          optionRender={option => {
            const { avatarSrc, backColor, foreColor, label } = option.data;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SimpleAvatar
                  size={24}
                  src={avatarSrc}
                  name={label}
                  backColor={backColor}
                  foreColor={foreColor}
                  showBorder={false}
                  showShadow={false}
                />
                <span>{label}</span>
              </div>
            );
          }}
        />
      </Form.Item>

      {/* Group selector — shown only when editing an orphaned board (no group assigned) */}
      {isOrphaned && (
        <Form.Item label={translateX('group')} required>
          <Select
            value={resolvedGroupUid || undefined}
            onChange={setResolvedGroupUid}
            placeholder={translateX('select_group')}
            options={(boardGroups || []).map(g => ({ value: g.uid || g._id, label: g.label }))}
          />
        </Form.Item>
      )}

      {isEditing && (
        <div
          className="mt-3"
          style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8 }}
        >
          <Button type="dashed" icon={<InboxOutlined />} onClick={onArchive} block>
            {translateX('archive_board')}
          </Button>
          <Button type="primary" danger icon={<DeleteOutlined />} onClick={onDelete} block>
            {translateX('delete_this_board')}
          </Button>
        </div>
      )}
    </Form>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ParametersTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getAvatarSrc(member) {
  const avatar = member?.avatar;
  return avatar?.base64Thumb || avatar?.thumbUrl || avatar?.storageUrl || member?.img || null;
}

function buildTeamOptions(team = []) {
  return team
    .filter(m => m.status === 2)
    .map(m => ({
      value: m._id || m.accountId,
      label: m.name || m.fullName || '',
      text: m.name || m.fullName || '',
      avatarSrc: getAvatarSrc(m),
      backColor: m.backColor || '#e6f4ff',
      foreColor: m.foreColor || '#0958d9',
    }));
}
